/** FIT device identification retained with a normalized ride. */
export interface RecordingDevice {
  readonly manufacturer?: string | number;
  readonly productId?: number;
  readonly productName?: string;
}

const DEVICE_FIELDS = new Set(["manufacturer", "productId", "productName"]);
const validId = (value: unknown): value is number => (
  typeof value === "number" && Number.isInteger(value) && value >= 0 && value < 65_535
);
const validName = (value: unknown): value is string => (
  typeof value === "string" && value.trim().length > 0 && value.length <= 120
  && !/[\u0000-\u001f\u007f]/u.test(value)
);

/** Validates metadata at both the service and browser render boundaries. */
export function isRecordingDevice(value: unknown): value is RecordingDevice {
  if (value === null || typeof value !== "object" || Array.isArray(value)) return false;
  const device = value as RecordingDevice;
  return Object.keys(device).every(key => DEVICE_FIELDS.has(key))
    && (device.manufacturer === undefined || validName(device.manufacturer) || validId(device.manufacturer))
    && (device.productId === undefined || validId(device.productId))
    && (device.productName === undefined || validName(device.productName))
    && (device.manufacturer !== undefined || device.productId !== undefined || device.productName !== undefined);
}

/** Formats the available identity without inventing an unknown model. */
export function recordingDeviceLabel(device: RecordingDevice): string {
  const manufacturer = typeof device.manufacturer === "number"
    ? `Manufacturer ${device.manufacturer}`
    : device.manufacturer === "omata" ? "OMATA"
      : (device.manufacturer ?? "").replace(/_/g, " ").replace(/\b\w/g, letter => letter.toUpperCase());
  const product = device.productName ?? (device.productId === undefined ? "" : `Product ${device.productId}`);
  const label = manufacturer && product.toLowerCase().startsWith(`${manufacturer.toLowerCase()} `)
    ? product : [manufacturer, product].filter(Boolean).join(" ");
  return label.replace(/^Dynastream\b/i, "OMATA");
}
